// useRegisterScroll — Back puts a register where the operator left it
// (W-21 / X-18).
//
// A manager checking records one by one opens a record's report, map or
// detail page and presses Back. Two things threw their place away:
//   - App's <ScrollToTop/> resets the window AND every `.overflow-y-auto` on
//     each pathname change, so the list came back at the top (measured: the
//     Scouting register 3 058px → 0 on a phone, Compliance 1 511px → 0);
//   - the filters lived in useState and were gone with the unmount — that half
//     is useUrlState's (`{ elide: true }`), this hook is only the scroll.
//
// What is remembered is a ROW, not a pixel offset: the topmost row on screen
// and how far below the scroller's top edge it sat. On return the scroller is
// moved until that row sits at the same offset again, so rows that arrive
// above it (a fresh record, a pending-sync card, a banner) cannot push the
// operator's place away. The raw offset is the fallback when the row is gone.
//
// The memory is keyed on the register AND its URL — pathname plus the query,
// which is where the filters now live — and is only USED when the page is
// reached by Back: a history pop, or the replace a `backTo`/returnTo Back
// performs. A fresh arrival (a nav-bar tap, a link: a PUSH) starts at the top,
// as it should. It is kept in memory for this page load only, like W-14's
// useRememberedWindowScroll, whose settle loop this shares.
//
// Rows opt in with `{...registerRow(id)}`. The scroller is the register's own
// `overflow-y-auto` element (pass its ref) or, when the page grows and the
// WINDOW scrolls, omit it and pass the page root instead.

import { useEffect, useRef, type RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { settleScroll, windowBox, elementBox, type ScrollBox } from './useRememberedWindowScroll'

export const ROW_ATTR = 'data-register-row'

/** Spread onto a register row so Back can find it again by id. */
export function registerRow(id: string): Record<typeof ROW_ATTR, string> {
  return { [ROW_ATTR]: id }
}

type Spot = { y: number; id: string | null; offset: number }
const spots = new Map<string, Spot>()
const MAX_SPOTS = 200

/** Test seam — the memory is module-level by design. */
export function forgetRegisterScroll(): void {
  spots.clear()
}

function remember(k: string, s: Spot) {
  spots.delete(k)
  spots.set(k, s)
  if (spots.size > MAX_SPOTS) spots.delete(spots.keys().next().value as string)
}

const escapeId = (id: string) =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id.replace(/["\\]/g, '\\$&')

type Frame = { box: ScrollBox; viewTop: () => number; viewH: () => number; rows: ParentNode; live: () => boolean }

function frameOf(scroller: HTMLElement | null, root: HTMLElement | null): Frame {
  if (scroller) {
    return {
      box: elementBox(scroller),
      viewTop: () => scroller.getBoundingClientRect().top,
      viewH: () => scroller.clientHeight,
      rows: scroller,
      // clientHeight 0 = hidden (a Suspense fallback hides the old page with
      // display:none while the next one loads) — its scrollTop reads 0 then.
      live: () => scroller.isConnected && scroller.clientHeight > 0,
    }
  }
  return {
    box: windowBox,
    viewTop: () => 0,
    viewH: () => window.innerHeight,
    rows: root ?? document,
    live: () => !!root && root.isConnected && root.getClientRects().length > 0,
  }
}

/** The topmost row whose bottom is below the view's top edge. Rows are in
 *  document order down the page, so a binary search finds it. */
function topRow(f: Frame): { id: string; offset: number } | null {
  const rows = f.rows.querySelectorAll<HTMLElement>(`[${ROW_ATTR}]`)
  const top = f.viewTop()
  let lo = 0, hi = rows.length - 1, hit = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (rows[mid].getBoundingClientRect().bottom > top + 1) { hit = mid; hi = mid - 1 } else lo = mid + 1
  }
  if (hit < 0) return null
  const r = rows[hit]
  return { id: r.getAttribute(ROW_ATTR) ?? '', offset: r.getBoundingClientRect().top - top }
}

function spotOf(f: Frame): Spot {
  const row = topRow(f)
  return { y: f.box.get(), id: row?.id ?? null, offset: row?.offset ?? 0 }
}

/** Where to scroll so the saved row sits where it sat; the saved offset when
 *  the row is not on the page any more. */
function spotTarget(f: Frame, s: Spot): number {
  const row = s.id ? f.rows.querySelector<HTMLElement>(`[${ROW_ATTR}="${escapeId(s.id)}"]`) : null
  if (!row) return s.y
  return Math.max(0, f.box.get() + row.getBoundingClientRect().top - f.viewTop() - s.offset)
}

/** Where to scroll so row `id` is in the middle of the view (no memory, but a
 *  deep link named a row). Stays put when the row is not on the page. */
function centreTarget(f: Frame, id: string): number {
  const row = f.rows.querySelector<HTMLElement>(`[${ROW_ATTR}="${escapeId(id)}"]`)
  if (!row) return f.box.get()
  const r = row.getBoundingClientRect()
  return Math.max(0, f.box.get() + r.top - f.viewTop() - Math.max(0, (f.viewH() - r.height) / 2))
}

export function useRegisterScroll(opts: {
  /** Names the register — one memory per register × URL. */
  key: string
  /** The rows are rendered. Restoring waits for this. */
  ready: boolean
  /** The register's own scroller. Omit when the WINDOW scrolls. */
  scrollerRef?: RefObject<HTMLElement>
  /** The page root (window-scrolled pages): scrolls are recorded only while it
   *  is on screen, so the next page's scroll is never taken for this one's. */
  rootRef?: RefObject<HTMLElement>
  /** A row to bring into view when there is nothing remembered — the row a
   *  deep link opened (`?block=`, `?observation=`). */
  focusId?: string | null
}): void {
  const { key, ready, scrollerRef, rootRef, focusId = null } = opts
  const { pathname, search } = useLocation()
  const navType = useNavigationType()
  const memKey = `${key}|${pathname}${search}`

  // Decided on the FIRST render, before any scroll event of this mount can
  // overwrite the memory (ScrollToTop's reset fires one).
  const plan = useRef<{ spot: Spot | null; focus: string | null } | null | undefined>(undefined)
  if (plan.current === undefined) {
    const spot = navType === 'PUSH' ? null : spots.get(memKey) ?? null
    plan.current = spot || focusId ? { spot, focus: focusId } : null
  }

  const memKeyRef = useRef(memKey)
  const frame = () => frameOf(scrollerRef?.current ?? null, rootRef?.current ?? null)

  // Record: on scroll (one read per frame), and on the tap or key that leaves
  // the page — synchronously, before the route changes under it. Re-armed when
  // the rows arrive: a page that shows a spinner first (an early return) has
  // no root or scroller at mount, and a frame read then would never be live.
  useEffect(() => {
    const f = frameOf(scrollerRef?.current ?? null, rootRef?.current ?? null)
    const source: EventTarget = scrollerRef?.current ?? window
    let raf = 0
    const snap = () => {
      raf = 0
      if (f.live()) remember(memKeyRef.current, spotOf(f))
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(snap) }
    source.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('pointerdown', snap, true)
    document.addEventListener('keydown', snap, true)
    return () => {
      source.removeEventListener('scroll', onScroll)
      document.removeEventListener('pointerdown', snap, true)
      document.removeEventListener('keydown', snap, true)
      cancelAnimationFrame(raf)
    }
  }, [scrollerRef, rootRef, ready])

  // A filter or an opened row rewrote the URL: this place now belongs to the
  // new URL too — it is the one Back will come back to.
  useEffect(() => {
    if (memKeyRef.current === memKey) return
    memKeyRef.current = memKey
    const f = frame()
    if (f.live()) remember(memKey, spotOf(f))
  }, [memKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore, once per mount, when the rows are there. The cleanup only
  // CANCELS (StrictMode's double mount, or leaving mid-settle), which keeps
  // the plan for the next run.
  useEffect(() => {
    const p = plan.current
    if (!ready || !p) return
    const f = frame()
    const target = p.spot ? () => spotTarget(f, p.spot!) : () => centreTarget(f, p.focus!)
    return settleScroll(f.box, target, () => { plan.current = null })
  }, [ready]) // eslint-disable-line react-hooks/exhaustive-deps
}
